/**
 * Wat de extensie afloopt, één rij per zoekopdracht: aan of uit, de buurten als
 * chips, het ritme, hoeveel woningen de feed nu telt, de laatste ronde en wat
 * die opleverde, en één woord over de stand. Het potloodje opent het paneel.
 *
 * Dezelfde tabelklasse als de datasets erboven, dus dezelfde dichtheid.
 */
import {
  formatAantal,
  formatDatumTijd,
  gebiedLabel,
  ritmeLabel,
  type Zoekopdracht,
} from '@mamimap/shared';
import { Pencil } from 'lucide-react';
import { overlapPerZoekopdracht, relatieveTijd, type StandToon, zoekopdrachtStand } from './stand';

type ZoekopdrachtTabelProps = {
  rijen: readonly Zoekopdracht[];
  /** Slug -> naam zoals Funda hem schrijft. */
  namen: ReadonlyMap<string, string>;
  onBewerk: (zoekopdracht: Zoekopdracht) => void;
  onWissel: (zoekopdracht: Zoekopdracht, actief: boolean) => void;
  /** De rij waar een wijziging voor onderweg is; die krijgt even geen tweede. */
  bezigId: string | null;
};

/** Meer chips dan dit worden "+n": een rij is 26 px en geen alinea. */
const MAX_CHIPS = 4;

const TOON_KLASSE: Record<StandToon, string> = {
  rustig: 'text-muted',
  'let-op': 'text-brick',
  uit: 'text-faint',
};

export function ZoekopdrachtTabel({
  rijen,
  namen,
  onBewerk,
  onWissel,
  bezigId,
}: ZoekopdrachtTabelProps) {
  const overlap = overlapPerZoekopdracht(rijen);
  const nu = new Date();

  return (
    <div className="overflow-x-auto border border-line bg-surface-strong">
      <table className="tabel">
        <thead>
          <tr>
            <th className="w-7">
              <span className="sr-only">Aan</span>
            </th>
            <th>Naam</th>
            <th>Gebieden</th>
            <th>Ritme</th>
            <th className="text-right">Woningen</th>
            <th>Laatste ronde</th>
            <th>Uitslag</th>
            <th>Stand</th>
            <th className="w-7">
              <span className="sr-only">Bewerken</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => {
            const stand = zoekopdrachtStand(rij, nu);
            const anderen = overlap.get(rij.id);
            const extra = rij.gebieden.length - MAX_CHIPS;
            return (
              <tr key={rij.id} className={rij.actief ? undefined : 'text-faint'}>
                <td className="pr-0">
                  <input
                    type="checkbox"
                    checked={rij.actief}
                    disabled={bezigId === rij.id}
                    onChange={(event) => onWissel(rij, event.target.checked)}
                    aria-label={`${rij.naam} ${rij.actief ? 'uitzetten' : 'aanzetten'}`}
                    className="h-3.5 w-3.5 accent-accent align-middle"
                  />
                </td>
                <td className={rij.actief ? 'text-ink' : undefined}>{rij.naam}</td>
                <td>
                  <span className="flex flex-wrap gap-1 py-[2px]">
                    {rij.gebieden.slice(0, MAX_CHIPS).map((slug) => (
                      <span key={slug} className="chip" title={slug}>
                        {gebiedLabel(slug, namen)}
                      </span>
                    ))}
                    {extra > 0 ? (
                      <span className="chip" title={rij.gebieden.slice(MAX_CHIPS).join(', ')}>
                        +{extra}
                      </span>
                    ) : null}
                    {rij.gebieden.length === 0 ? <span className="text-faint">–</span> : null}
                  </span>
                </td>
                <td>{ritmeLabel(rij.intervalMinuten)}</td>
                <td className="cijfers text-right">{formatAantal(rij.aantalWoningen)}</td>
                <td
                  className="cijfers"
                  title={rij.laatsteRunOp ? formatDatumTijd(rij.laatsteRunOp) : undefined}
                >
                  {relatieveTijd(rij.laatsteRunOp, nu)}
                  {/* Wie de ronde liep (MA-35): "3 uur geleden · kloud". Leeg als
                      de extensie geen naam meestuurde; er wordt niets aangenomen. */}
                  {rij.laatsteRun?.vanger ? (
                    <span className="text-muted">{` · ${rij.laatsteRun.vanger}`}</span>
                  ) : null}
                </td>
                <td className="max-w-72 truncate" title={rij.laatsteUitslag ?? undefined}>
                  {rij.laatsteUitslag ?? <span className="text-faint">–</span>}
                </td>
                <td className={TOON_KLASSE[stand.toon]}>
                  {stand.tekst}
                  {anderen ? (
                    <span className="text-muted">{` · overlapt met ${anderen.join(', ')}`}</span>
                  ) : null}
                </td>
                <td className="pr-1">
                  <button
                    type="button"
                    onClick={() => onBewerk(rij)}
                    aria-label={`${rij.naam} bewerken`}
                    className="text-faint hover:text-ink"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
